import { IDataPoint, ISummaryHistory } from 'store/deal-evaluation/types';
import _ from 'lodash';
import { calculateNumOfMonthsUsingDates } from 'lib/helpers/date-range';

/**
 * Takes two arrays and joins them for the D3 line function
 *
 * Input:
 * param1: ['01/2000', '01/2001', '01/2002']
 * param2: [34.4, 53.1, 65.3]
 *
 * Output:
 * [{ x: '01/2000', y: 34.4 }, { x: '01/2001', y: 53.1 }, { x: '01/2002', y: 65.3 }]
 *
 * @param xAxisData
 * @param yAxisData
 */
export function joinData(xAxisData: Date[], yAxisData: number[]): IDataPoint[] {
  return xAxisData.map((xd: Date, index: number) => ({
    x: xd,
    y: yAxisData[index],
  }));
}

/**
 * Takes an array of lines and then returns the max y value
 *
 * @param lines An array of lines
 * @returns A number representing the max y-value from all of the lines
 *
 * @example_input
 * [
 *    [ {x: 1, y: 2}, {x: 2, y: 3}, ... ]
 *    [ {x: 1, y: 2}, {x: 2, y: 3}, ... ]
 * ]
 *
 * @example_output
 * 30.1
 */
export function getMaxValueFromArrayOfLines(lines: IDataPoint[][]): number {
  let max = 0;
  lines.forEach((line: IDataPoint[]) => {
    line.forEach((d: IDataPoint) => {
      if (d?.y && d?.y > max) {
        max = d?.y;
      }
    });
  });
  return max;
}

export function getMinValueFromArrayOfLines(lines: IDataPoint[][]): number {
  let min = 0;
  lines.forEach((line: IDataPoint[]) => {
    line.forEach((d: IDataPoint) => {
      if (d?.y && d?.y < min) {
        min = d?.y;
      }
    });
  });
  return min;
}

// Crop historical data length to match that of forecast
// E.g., if forecast is 1 year, cut historical to 1 year
export function calculateBalancedHistory(
  history: ISummaryHistory[],
  forecastMonths: number,
  periodStart: Date,
  periodEnd: Date
) {
  const historyBalanced = _.cloneDeep(history);
  const historyDurationInMonths = history.length;
  const delta = historyDurationInMonths - forecastMonths;

  // Show only contract period if no forecast exists
  if (!forecastMonths) {
    // Calculate months using period start and end
    const contractPeriodInMonths = calculateNumOfMonthsUsingDates(periodStart, periodEnd);
    const deltaNoForecast = historyDurationInMonths - contractPeriodInMonths;
    for (let i = 0; i < deltaNoForecast; i++) {
      historyBalanced.shift();
    }
  } else if (delta > 0) {
    // Preserve only the n (forecastMonths) number of months of history
    // beginning with n-1 back towards 0
    //
    // Loop through each history index beginning with zero index
    // and shift out the elements from the front until the desired
    // months are remaining
    for (let i = 0; i < delta; i++) {
      historyBalanced.shift();
    }
  }

  return historyBalanced;
}
